import { sha256 } from "@noble/hashes/sha256";
import { bytesToHex } from "@noble/hashes/utils";
import {
  getSeedanceModelCapability,
  validateSeedanceReference,
  type AssetView,
  type CanvasDocument,
  type SeedanceCapabilityCatalog,
  type SeedanceNodeSettings
} from "@seedance/contracts";
import type { ReferenceKind, ReferenceMapItem } from "./reference-map.js";

const compilerVersion = "2026-08-18";
const referenceKinds: readonly ReferenceKind[] = ["image", "video", "audio"];

type SeedanceNode = Extract<CanvasDocument["nodes"][number], { kind: "seedance" }>;
type MediaNode = Extract<CanvasDocument["nodes"][number], { kind: ReferenceKind }>;
type PromptNode = Extract<CanvasDocument["nodes"][number], { kind: "text" | "script" }>;

export interface AuthorizedSeedanceAsset {
  asset: AssetView;
  ownerId: string;
  /** True only for a still-valid non-owner team grant. Owners are always readable while active. */
  readable: boolean;
}

export interface CompiledSeedanceAssetReference {
  assetId: string;
  nodeId: string;
  edgeId: string;
  kind: ReferenceKind;
  role: "first_frame" | "last_frame" | "reference_image" | "reference_video" | "reference_audio";
  mode?: ReferenceMapItem["mode"];
  instruction: string;
  order: number;
}

export interface CompiledSeedanceRequest {
  model: SeedanceNodeSettings["model"];
  ratio: SeedanceNodeSettings["ratio"];
  durationSeconds: number;
  resolution: SeedanceNodeSettings["resolution"];
  watermark: boolean;
  generateAudio: boolean;
  seed: number;
  assets: readonly CompiledSeedanceAssetReference[];
}

export interface CompileSeedanceSuccess {
  ok: true;
  compilerVersion: string;
  capabilityVersion: string;
  finalPrompt: string;
  request: CompiledSeedanceRequest;
  references: readonly ReferenceMapItem[];
  fingerprint: string;
}

export interface CompileSeedanceFailure {
  ok: false;
  code:
    | "SEEDANCE_NODE_NOT_FOUND"
    | "OUTGOING_SEEDANCE_EDGE"
    | "INVALID_REFERENCE_SOURCE"
    | "INVALID_REFERENCE_BINDING"
    | "MISSING_ASSET"
    | "UNAUTHORIZED_ASSET"
    | "INVALID_ASSET"
    | "UNSUPPORTED_MODEL"
    | "INVALID_MODEL_SETTINGS"
    | "UNSUPPORTED_REFERENCE_ROLE"
    | "REFERENCE_LIMIT"
    | "PROMPT_REQUIRED"
    | "PROMPT_TOO_LONG";
  message: string;
  nodeIds: readonly string[];
  edgeIds: readonly string[];
  request: undefined;
}

export type CompileSeedanceResult = CompileSeedanceSuccess | CompileSeedanceFailure;

export interface CompileSeedanceRequestInput {
  document: CanvasDocument;
  seedanceNodeId: string;
  ownerId: string;
  capabilities: Readonly<SeedanceCapabilityCatalog>;
  assets: ReadonlyMap<string, AuthorizedSeedanceAsset>;
}

function failure(
  code: CompileSeedanceFailure["code"],
  message: string,
  nodeIds: readonly string[] = [],
  edgeIds: readonly string[] = []
): CompileSeedanceFailure {
  return { ok: false, code, message, nodeIds, edgeIds, request: undefined };
}

function compareEdges<T extends { id: string; order: number }>(left: T, right: T): number {
  return left.order - right.order || left.id.localeCompare(right.id);
}

function promptText(node: PromptNode): string {
  return node.kind === "text"
    ? node.text.trim()
    : [node.title, node.synopsis].filter(Boolean).join("\n").trim();
}

function isMediaNode(node: CanvasDocument["nodes"][number] | undefined): node is MediaNode {
  return node?.kind === "image" || node?.kind === "video" || node?.kind === "audio";
}

function isPromptNode(node: CanvasDocument["nodes"][number] | undefined): node is PromptNode {
  return node?.kind === "text" || node?.kind === "script";
}

function requestRole(reference: ReferenceMapItem): CompiledSeedanceAssetReference["role"] {
  if (reference.kind === "image") {
    return reference.role === "first_frame" || reference.role === "last_frame"
      ? reference.role
      : "reference_image";
  }
  return reference.kind === "video" ? "reference_video" : "reference_audio";
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(",")}}`;
}

function fingerprint(value: unknown): string {
  return bytesToHex(sha256(new TextEncoder().encode(canonicalJson(value))));
}

/** @internal Shared V2/V3 model validation, independent of prompt representation. */
export function validateSettings(
  settings: Omit<SeedanceNodeSettings, "prompt">,
  capabilities: Readonly<SeedanceCapabilityCatalog>
): CompileSeedanceFailure | undefined {
  const capability = getSeedanceModelCapability(capabilities, settings.model);
  if (!capability) {
    return failure("UNSUPPORTED_MODEL", "所选 Seedance 模型不在当前能力目录中。", [], []);
  }
  const duration = capability.durationSeconds;
  const valid = capabilities.ratios.includes(settings.ratio)
    && capability.resolutions.includes(settings.resolution)
    && Number.isInteger(settings.durationSeconds)
    && settings.durationSeconds >= duration.minimum
    && settings.durationSeconds <= duration.maximum
    && (settings.durationSeconds - duration.minimum) % duration.step === 0
    && Number.isInteger(settings.seed)
    && settings.seed >= capabilities.seed.minimum
    && settings.seed <= capabilities.seed.maximum
    && (!settings.generateAudio || capability.supportsAudio)
    && (!settings.watermark || capability.supportsWatermark);
  return valid
    ? undefined
    : failure("INVALID_MODEL_SETTINGS", "所选模型不支持当前分辨率、时长或开关配置。", [], []);
}

/** @internal Authorization is always checked for every connected reference. */
export function validateCompiledAsset(
  reference: Pick<ReferenceMapItem, "assetId" | "nodeId" | "edgeId" | "kind">,
  targetNodeId: string,
  ownerId: string,
  assets: CompileSeedanceRequestInput["assets"],
  capabilities: CompileSeedanceRequestInput["capabilities"]
): CompileSeedanceFailure | undefined {
  const authorized = assets.get(reference.assetId);
  const nodeIds = [reference.nodeId, targetNodeId];
  const edgeIds = [reference.edgeId];
  if (!authorized) {
    return failure("MISSING_ASSET", "画布参考素材不存在或已不可用。", nodeIds, edgeIds);
  }
  if (authorized.ownerId !== ownerId && !authorized.readable) {
    return failure("UNAUTHORIZED_ASSET", "当前账号没有使用该参考素材的权限。", nodeIds, edgeIds);
  }
  if (authorized.asset.trashedAt !== null || authorized.asset.kind !== reference.kind) {
    return failure("INVALID_ASSET", "参考素材已删除、不可用或与画布节点类型不一致。", nodeIds, edgeIds);
  }
  if (BigInt(authorized.asset.sizeBytes) > BigInt(capabilities.fileLimitsBytes[reference.kind])) {
    return failure("INVALID_ASSET", "参考素材超过当前模型允许的文件大小。", nodeIds, edgeIds);
  }
  return undefined;
}

/**
 * Deterministically compiles a saved canvas into a provider-independent Seedance
 * request snapshot. It never creates URLs, accesses storage, or calls a provider.
 */
export function compileSeedanceRequest({
  document,
  seedanceNodeId,
  ownerId,
  capabilities,
  assets
}: CompileSeedanceRequestInput): CompileSeedanceResult {
  const nodesById = new Map(document.nodes.map((node) => [node.id, node]));
  const target = nodesById.get(seedanceNodeId);
  if (target?.kind !== "seedance") {
    return failure("SEEDANCE_NODE_NOT_FOUND", "未找到要编译的 Seedance 节点。", [seedanceNodeId]);
  }
  const settingsFailure = validateSettings(target.settings, capabilities);
  if (settingsFailure) return { ...settingsFailure, nodeIds: [target.id] };

  const outgoing = document.edges
    .filter((edge) => edge.sourceNodeId === target.id)
    .sort(compareEdges)[0];
  if (outgoing) {
    return failure(
      "OUTGOING_SEEDANCE_EDGE",
      "Seedance 生成节点只能接收输入，不能作为参考素材的输出端。",
      [target.id, outgoing.targetNodeId],
      [outgoing.id]
    );
  }

  const incoming = document.edges
    .filter((edge) => edge.targetNodeId === target.id)
    .sort(compareEdges);
  const promptFragments: string[] = [target.settings.prompt.trim()];
  const mediaReferences: ReferenceMapItem[] = [];
  for (const edge of incoming) {
    const source = nodesById.get(edge.sourceNodeId);
    if (isPromptNode(source)) {
      const fragment = promptText(source);
      if (fragment) promptFragments.push(fragment);
      continue;
    }
    if (!isMediaNode(source)) {
      return failure(
        "INVALID_REFERENCE_SOURCE",
        "Seedance 输入只能来自文字、脚本、图片、视频或音频节点。",
        [edge.sourceNodeId, target.id],
        [edge.id]
      );
    }
    const assetFailure = validateCompiledAsset({ assetId: source.assetId, nodeId: source.id, edgeId: edge.id, kind: source.kind }, target.id, ownerId, assets, capabilities);
    if (assetFailure) return assetFailure;
    mediaReferences.push({
      assetId: source.assetId,
      nodeId: source.id,
      edgeId: edge.id,
      kind: source.kind,
      role: edge.role,
      ...(edge.mode === undefined ? {} : { mode: edge.mode }),
      instruction: edge.instruction,
      order: edge.order,
      mention: "@图片1"
    });
  }

  const mentionCounts: Record<ReferenceKind, number> = { image: 0, video: 0, audio: 0 };
  const references = mediaReferences
    .sort((left, right) => left.order - right.order || left.edgeId.localeCompare(right.edgeId))
    .map((reference) => {
      mentionCounts[reference.kind] += 1;
      const mentionIndex = mentionCounts[reference.kind];
      return {
        ...reference,
        mention: `@${reference.kind === "image" ? "图片" : reference.kind === "video" ? "视频" : "音频"}${mentionIndex}` as ReferenceMapItem["mention"]
      };
    });
  return finalizeCompiledSeedanceRequest({ target, capabilities, references,
    finalPrompt: promptFragments.filter(Boolean).join("\n\n"), compilerVersion });
}

/** @internal Inputs must already have passed graph, model and asset validation.
 * No graph traversal or ordinal interpretation occurs here. */
interface SeedanceRequestContent {
  target: { id: string; settings: Omit<SeedanceNodeSettings, "prompt"> };
  capabilities: CompileSeedanceRequestInput["capabilities"];
  references: readonly ReferenceMapItem[];
  finalPrompt: string;
}

/** @internal Shared by preview checks and request finalization. Graph, settings and
 * assets must already be validated; limits, roles and prompt checks are never optional. */
export function validateSeedanceRequestContent({ target, capabilities, references, finalPrompt }: SeedanceRequestContent): CompileSeedanceFailure | undefined {
  const capability = getSeedanceModelCapability(capabilities, target.settings.model)!;
  if (references.length > capability.maxReferences) {
    return failure("REFERENCE_LIMIT", `参考素材总数不能超过 ${capability.maxReferences} 个。`, [target.id], references.map(({ edgeId }) => edgeId));
  }
  for (const kind of referenceKinds) {
    const kindReferences = references.filter((reference) => reference.kind === kind);
    if (kindReferences.length > capability.maxReferencesByKind[kind]) {
      return failure("REFERENCE_LIMIT", `${kind === "image" ? "图片" : kind === "video" ? "视频" : "音频"}参考数量超过当前模型限制。`, [target.id], kindReferences.map(({ edgeId }) => edgeId));
    }
    for (const reference of kindReferences) {
      const roleValidation = validateSeedanceReference(kind, reference.role, capability);
      if (!roleValidation.ready) {
        return failure("UNSUPPORTED_REFERENCE_ROLE", `${reference.mention} ${roleValidation.message!}`, [reference.nodeId, target.id], [reference.edgeId]);
      }
    }
  }

  if (finalPrompt.length === 0) {
    return failure("PROMPT_REQUIRED", "请填写提示词，或连接包含文字的文本/脚本节点。", [target.id]);
  }
  if (finalPrompt.length > 4_000) {
    return failure("PROMPT_TOO_LONG", "实际生成提示词不能超过 4000 个字符。", [target.id]);
  }
  return undefined;
}

/** @internal Produces a deterministic snapshot only after all content checks pass. */
export function finalizeCompiledSeedanceRequest(input: SeedanceRequestContent & { compilerVersion: string }): CompileSeedanceResult {
  const contentFailure = validateSeedanceRequestContent(input);
  if (contentFailure) return contentFailure;
  const { target, capabilities, references, finalPrompt, compilerVersion } = input;
  const request: CompiledSeedanceRequest = {
    model: target.settings.model,
    ratio: target.settings.ratio,
    durationSeconds: target.settings.durationSeconds,
    resolution: target.settings.resolution,
    watermark: target.settings.watermark,
    generateAudio: target.settings.generateAudio,
    seed: target.settings.seed,
    assets: references.map((reference) => ({
      assetId: reference.assetId,
      nodeId: reference.nodeId,
      edgeId: reference.edgeId,
      kind: reference.kind,
      role: requestRole(reference),
      ...(reference.mode === undefined ? {} : { mode: reference.mode }),
      instruction: reference.instruction,
      order: reference.order
    }))
  };
  const snapshot = {
    compilerVersion,
    capabilityVersion: capabilities.capabilityVersion,
    finalPrompt,
    request,
    references
  };
  return {
    ok: true,
    ...snapshot,
    fingerprint: fingerprint(snapshot)
  };
}
